import {
    Component,
    ElementRef,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { MainConfig } from '../../../conf/main.config';
import { LoginPolicy, LoginUser } from './../models';
import { AuthService } from './../auth.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit {
    readonly MainConfig = MainConfig;

    @ViewChild('loginTitle', { static: false }) loginTitle: ElementRef;
    @ViewChild('identifierInput', { static: false }) identifierInput: ElementRef;

    currentLanguage = 'pt-BR';
    user: LoginUser = { email: '', password: '' };
    recovery = { email: '' };

    showPassword = false;
    submitting = false;
    loginTried = false;

    recoverPassword = false;
    recovering = false;
    passwordResetEnabled = false;
    rememberSessionEnabled = false;
    rememberSession = false;
    policyLoaded = false;
    loginPolicy: LoginPolicy = null;

    errorMessage = '';
    successMessage = '';

    canSignup = MainConfig.signup !== 'never';

    constructor(
        protected http: HttpClient,
        private auth: AuthService,
        private router: Router,
        public activeModal: NgbActiveModal
    ) {
        this.currentLanguage =
            localStorage.getItem('language') === 'fr' ? 'fr' : 'pt-BR';
    }

    ngOnInit(): void {
        // O backend informa quais recursos de acesso estão realmente ativos.
        // Isso evita exibir recuperação de senha quando o serviço de e-mail
        // ou o endpoint correspondente estiver desativado.
        this.auth.getLoginPolicy().subscribe(
            (policy: LoginPolicy) => {
                this.loginPolicy = policy || null;
                this.passwordResetEnabled =
                    !!(policy && policy.passwordResetEnabled);
                this.rememberSessionEnabled =
                    !!(policy && policy.rememberSessionEnabled);
                this.policyLoaded = true;
            },
            () => {
                this.passwordResetEnabled = false;
                this.policyLoaded = true;
            }
        );

        setTimeout(() => this.focusTitle(), 0);
    }

    t(portuguese: string, french: string): string {
        return this.currentLanguage === 'fr' ? french : portuguese;
    }

    setLanguage(language: 'pt-BR' | 'fr'): void {
        this.currentLanguage = language;
        localStorage.setItem('language', language);
        document.documentElement.lang = language;
    }

    togglePasswordVisibility(): void {
        this.showPassword = !this.showPassword;
    }

    onLogin(form: NgForm): void {
        // A trava de envio evita cliques repetidos criarem várias requisições
        // enquanto a autenticação anterior ainda está em andamento.
        if (this.submitting) {
            return;
        }

        this.loginTried = true;
        this.errorMessage = '';
        this.successMessage = '';

        if (!form.valid) {
            // Mantém a validação próxima ao campo e leva o foco ao primeiro erro,
            // o que melhora teclado, leitor de tela e manutenção do fluxo.
            this.focusFirstInvalid(form);
            return;
        }

        this.submitting = true;

        this.auth.login(
            this.user,
            this.rememberSessionEnabled && this.rememberSession
        ).subscribe(
            (response) => {
                this.submitting = false;
                this.successMessage =
                    (response && response.message) ||
                    this.t('Acesso realizado com sucesso.', 'Connexion réussie.');

                const redirectUrl = this.auth.redirectUrl;
                this.auth.redirectUrl = null;

                setTimeout(() => {
                    this.activeModal.close('logged-in');
                    if (redirectUrl) {
                        // redirectUrl vem do RouterStateSnapshot e pode conter
                        // query string; navigateByUrl preserva a URL original.
                        this.router.navigateByUrl(redirectUrl);
                    }
                }, 700);
            },
            (error) => {
                this.submitting = false;
                this.errorMessage = this.extractErrorMessage(error);
                setTimeout(() => this.focusTitle(), 0);
            }
        );
    }

    openRecovery(): void {
        // Nunca abre uma recuperação fictícia. O recurso só fica disponível
        // quando a política retornada pelo servidor confirma que ele está ativo.
        if (!this.passwordResetEnabled) {
            return;
        }

        this.recoverPassword = true;
        this.errorMessage = '';
        this.successMessage = '';
        this.recovery.email = this.looksLikeEmail(this.user.email)
            ? this.user.email
            : '';
        setTimeout(() => this.focusTitle(), 0);
    }

    backToLogin(): void {
        this.recoverPassword = false;
        this.errorMessage = '';
        this.successMessage = '';
        setTimeout(() => {
            this.focusTitle();
            if (this.identifierInput) {
                this.identifierInput.nativeElement.focus();
            }
        }, 0);
    }

    onRecoverPassword(form: NgForm): void {
        if (
            !this.passwordResetEnabled ||
            this.recovering ||
            !form.valid
        ) {
            this.focusFirstInvalid(form);
            return;
        }

        this.recovering = true;
        this.errorMessage = '';
        this.successMessage = '';

        this.http
            .post(
                MainConfig.API_ENDPOINT + '/user/resetpasswd',
                { email: this.recovery.email }
            )
            .subscribe(
                (response: any) => {
                    this.recovering = false;
                    this.successMessage =
                        (response && response.message) ||
                        this.t(
                            'Se o endereço estiver cadastrado, você receberá as instruções disponíveis para recuperação.',
                            'Si cette adresse est enregistrée, vous recevrez les instructions de récupération disponibles.'
                        );
                },
                (error) => {
                    this.recovering = false;
                    this.errorMessage = this.extractErrorMessage(error);
                }
            );
    }

    openRegister(): void {
        this.activeModal.dismiss('switch-register');
    }

    closeToHome(): void {
        this.activeModal.dismiss('home');
        this.router.navigate(['/home']);
    }

    private extractErrorMessage(error: any): string {
        // Erros de rede e servidor usam uma mensagem própria para não confundir
        // indisponibilidade temporária com credenciais incorretas.
        if (!error || error.status === 0 || error.status >= 500) {
            return this.t(
                'Não foi possível entrar agora. Tente novamente.',
                'Connexion impossible pour le moment. Réessayez.'
            );
        }

        // O backend usa a mesma resposta para usuário inexistente, senha errada
        // e conta inativa, evitando revelar se uma conta específica existe.
        if (error.status === 400 || error.status === 401) {
            return this.t(
                'E-mail, usuário ou senha incorretos.',
                'E-mail, nom d’utilisateur ou mot de passe incorrect.'
            );
        }

        if (error.error && error.error.message) {
            return error.error.message;
        }

        return this.t(
            'Não foi possível entrar agora. Tente novamente.',
            'Connexion impossible pour le moment. Réessayez.'
        );
    }

    private focusFirstInvalid(form: NgForm): void {
        if (form && form.controls) {
            Object.keys(form.controls).forEach((key) => {
                if (form.controls[key] && form.controls[key].invalid) {
                    form.controls[key].markAsTouched();
                }
            });
        }

        setTimeout(() => {
            const invalid = document.querySelector(
                '.wp-login-form input.ng-invalid'
            ) as HTMLElement;
            if (invalid) {
                invalid.focus();
            }
        }, 0);
    }

    private focusTitle(): void {
        if (this.loginTitle && this.loginTitle.nativeElement) {
            this.loginTitle.nativeElement.focus();
        }
    }

    private looksLikeEmail(value: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
    }
}
